import { useCallback, useEffect, useState } from 'react';

import { ColorMode } from '../theme/buildTheme';

const STORAGE_KEY = 'finderColorMode';

const isColorMode = (value: unknown): value is ColorMode => value === 'light' || value === 'dark';

/** Stored preference, else the OS preference, else light. window-guarded for SSR/tests. */
function initialMode(): ColorMode {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isColorMode(stored)) return stored;
  } catch {
    // storage disabled — fall through to the OS preference
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Persisted light/dark mode with a prefers-color-scheme fallback on first visit. */
export const useColorMode = (): [ColorMode, () => void] => {
  const [mode, setMode] = useState<ColorMode>(initialMode);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // best-effort: preference just won't persist
    }
  }, [mode]);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return [mode, toggleMode];
};
